import { getPositionFromIndex, createAnchor, getOverlappingAnchors, isAlreadyAnchored, isRejectedByLivePosition } from "../components/AutoSuggestion";
import { findCommandorNot, capitalizeInsideBraces, LineCommented, isCommented, isLineCommented, findTabularEnvs, parseColumnSpec, createGranularAnchors, numberToWords } from "../components/ReusableFunctions";
import { getInsideTextofBraces } from "../components/ReusableFunctions";

export const commonStyles = {
    dashclassification: {
        name: "Normalize Dash Between Numbers",
        description: "Change dash, en-dash, em-dash with spaces to a single '-' between numbers",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();
            const pattern = /(\d+)\s*[-–—]\s*(\d+)/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}-${match[2]}`;

                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
}
export const eplStyles = {
    ThousandSeparator: {
        name: "Thousand separator",
        description: "Add commas to numbers for thousands separation, skip 4-digit numbers",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/commaInNumberBefore.png",
        afterImage: "/images/description/commaInNumberAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            function getMathRanges(text) {
                const ranges = [];
                const mathEnvs = [
                    'equation', 'equation\\*', 'align', 'align\\*',
                    'alignat', 'alignat\\*', 'gather', 'gather\\*',
                    'multline', 'multline\\*', 'flalign', 'flalign\\*',
                    'eqnarray', 'eqnarray\\*', 'array', 'split',
                    'cases', 'dcases', 'math', 'displaymath'
                ];
                mathEnvs.forEach(env => {
                    const regex = new RegExp(`\\\\begin\\{${env}\\}[\\s\\S]*?\\\\end\\{${env}\\}`, 'g');
                    let m;
                    while ((m = regex.exec(text)) !== null) {
                        ranges.push([m.index, m.index + m[0].length]);
                    }
                });
                let m;
                const displayMath = /\\\[[\s\S]*?\\\]/g;
                while ((m = displayMath.exec(text)) !== null) ranges.push([m.index, m.index + m[0].length]);
                const inlineMath = /(?<!\$)\$(?!\$)[\s\S]*?(?<!\$)\$(?!\$)/g;
                while ((m = inlineMath.exec(text)) !== null) ranges.push([m.index, m.index + m[0].length]);
                const displayDollar = /\$\$[\s\S]*?\$\$/g;
                while ((m = displayDollar.exec(text)) !== null) ranges.push([m.index, m.index + m[0].length]);
                const inlineParen = /\\\([\s\S]*?\\\)/g;
                while ((m = inlineParen.exec(text)) !== null) ranges.push([m.index, m.index + m[0].length]);
                return ranges;
            }

            function isInsideMathRange(index, ranges) {
                return ranges.some(([start, end]) => index >= start && index < end);
            }

            const mathRanges = getMathRanges(texFile);
            const staleIds = new Set();
            const regex = /(?<![.,-])\b\d{4,}\b/g;
            let match;

            while ((match = regex.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = fullMatch.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                if (isInsideMathRange(match.index, mathRanges)) continue;

                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                // ── Single reliable rejection check ──
                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                // Inside your execute loop in the style object:
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },


    PFormatting: {
        name: "Formatting the p",
        description: "Change standalone statistical p to italic 𝑝",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/PFomattingBefore.png",
        afterImage: "/images/description/PFomattingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();
            const pattern = /(?<![A-Za-z])p(?=\s)/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = "𝑝";
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;


                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);

                if (overlapping.length > 0) {
                    overlapping.forEach(old => {
                        if (old.originalText === fullMatch) {
                            if (!session.getMarkers()[old.markerId]) {
                                old.markerId = session.addMarker(
                                    new Range(position.startRow, position.startCol, position.endRow, position.endCol),
                                    this.markerClass,
                                    "text"
                                );
                            }
                        } else {
                            staleIds.add(old.id);
                        }
                    });
                    if (overlapping.every(old => old.originalText === fullMatch)) continue;
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    TitleFormatting: {
        name: "Title Formatting",
        description: "Lowercase title, convert math to \\mth, chemistry to \\chem, update shorttitle",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();

            // ── Extract content inside \cmd{...} with brace matching ──
            function extractBraceContent(text, startIndex) {
                let depth = 1, i = startIndex, content = '';
                while (i < text.length && depth > 0) {
                    if (text[i] === '{') depth++;
                    else if (text[i] === '}') { depth--; if (depth === 0) break; }
                    content += text[i]; i++;
                }
                return { content, end: i };
            }

            // ── Tokenize content into: text, math ($...$), cmd (\cmd{...}) ──
            function tokenize(content) {
                const tokens = [];
                let pos = 0;

                while (pos < content.length) {
                    // $$ display math
                    if (content[pos] === '$' && content[pos + 1] === '$') {
                        const end = content.indexOf('$$', pos + 2);
                        if (end !== -1) {
                            tokens.push({ type: 'math', value: content.slice(pos, end + 2) });
                            pos = end + 2; continue;
                        }
                    }
                    // $ inline math
                    if (content[pos] === '$') {
                        const end = content.indexOf('$', pos + 1);
                        if (end !== -1) {
                            tokens.push({ type: 'math', value: content.slice(pos, end + 1) });
                            pos = end + 1; continue;
                        }
                    }
                    // \cmd{...} or bare \cmd
                    if (content[pos] === '\\') {
                        let j = pos + 1;
                        while (j < content.length && /[a-zA-Z*]/.test(content[j])) j++;
                        const cmd = content.slice(pos + 1, j);
                        if (j < content.length && content[j] === '{') {
                            let d = 1, k = j + 1, inner = '';
                            while (k < content.length && d > 0) {
                                if (content[k] === '{') d++;
                                else if (content[k] === '}') { d--; if (d === 0) break; }
                                inner += content[k]; k++;
                            }
                            tokens.push({ type: 'cmd', cmd, inner, raw: content.slice(pos, k + 1) });
                            pos = k + 1; continue;
                        }
                        tokens.push({ type: 'bare', value: content.slice(pos, j) });
                        pos = j; continue;
                    }
                    // plain text
                    let j = pos;
                    while (j < content.length && content[j] !== '$' && content[j] !== '\\') j++;
                    if (j > pos) tokens.push({ type: 'text', value: content.slice(pos, j) });
                    pos = j;
                }
                return tokens;
            }

            // ── Lowercase text preserving first letter of entire title ──
            function lowercaseTokens(tokens) {
                let firstLetterDone = false;
                return tokens.map(tok => {
                    if (tok.type !== 'text') return tok;
                    let val = tok.value;
                    if (!firstLetterDone) {
                        const li = val.search(/[a-zA-Z]/);
                        if (li !== -1) {
                            val = val.slice(0, li) +
                                val[li].toUpperCase() +
                                val.slice(li + 1).toLowerCase();
                            firstLetterDone = true;
                        } else {
                            val = val.toLowerCase();
                        }
                    } else {
                        val = val.toLowerCase();
                    }
                    return { ...tok, value: val };
                });
            }

            // ── Detect chemistry: text token ending with element + next is subscript math ──
            // e.g. text="H" next=$_2$ next-next=text starting with "O"
            function isChemSubscript(mathVal) {
                return /^\$_\{?\d+\}?\$$|^\$\^\{?\d+\}?\$$/.test(mathVal);
            }

            // ── Rebuild for \title — convert math to \mth, chemistry to \chem ──
            function buildTitleContent(tokens) {
                let result = '';
                let i = 0;
                while (i < tokens.length) {
                    const tok = tokens[i];

                    if (tok.type === 'text') {
                        // Check if this ends with a chemistry element before $_2$ style math
                        const chemMatch = tok.value.match(/^(.*?)([A-Z][a-z]?)$/s);
                        const nextTok = tokens[i + 1];
                        const afterNext = tokens[i + 2];

                        if (chemMatch && nextTok?.type === 'math' && isChemSubscript(nextTok.value)) {
                            // Extract subscript number
                            const sub = nextTok.value.replace(/^\$_?\{?/, '').replace(/\}?\$$/, '');
                            const elem = chemMatch[2];
                            // Collect trailing element from text after subscript
                            let trail = '';
                            if (afterNext?.type === 'text') {
                                const trailMatch = afterNext.value.match(/^([A-Z][a-z]?)/);
                                if (trailMatch) {
                                    trail = trailMatch[1];
                                    tokens[i + 2] = {
                                        ...afterNext,
                                        value: afterNext.value.slice(trailMatch[0].length)
                                    };
                                }
                            }
                            result += chemMatch[1] + `\\chem{${elem}_${sub}${trail}}`;
                            i += 2; // skip text + math tokens
                            continue;
                        }
                        result += tok.value;

                    } else if (tok.type === 'math') {
                        // Regular math — convert $x$ → \mth{x}
                        const inner = tok.value.replace(/^\$/, '').replace(/\$$/, '');
                        result += `\\mth{${inner}}`;

                    } else if (tok.type === 'cmd') {
                        result += tok.raw;
                    } else if (tok.type === 'bare') {
                        result += tok.value;
                    }
                    i++;
                }
                return result;
            }

            // ── Rebuild for \shorttitle — keep math/chemistry as-is, only lowercase text ──
            function buildShortTitleContent(tokens) {
                return tokens.map(tok => {
                    if (tok.type === 'math') return tok.value;        // keep $...$ unchanged
                    if (tok.type === 'cmd') return tok.raw;          // keep \cmd{} unchanged
                    if (tok.type === 'bare') return tok.value;        // keep \cmd unchanged
                    return tok.value;                                  // lowercased text
                }).join('');
            }

            // ── Find \title{...} ──
            const titleMatch = /\\title\s*\{/.exec(texFile);
            if (!titleMatch) return staleIds;

            const titleContentStart = titleMatch.index + titleMatch[0].length;
            const { content: titleContent, end: titleEnd } =
                extractBraceContent(texFile, titleContentStart);

            const titleStart = titleMatch.index;
            const originalTitle = texFile.slice(titleStart, titleEnd + 1);

            // Tokenize original title content
            const rawTokens = tokenize(titleContent);
            const lowercasedTokens = lowercaseTokens(rawTokens);

            const newTitleContent = buildTitleContent([...lowercasedTokens]);
            const newShortTitleContent = buildShortTitleContent([...lowercasedTokens]);

            const newTitleTag = `\\title{${newTitleContent}}`;

            // ── Find \shorttitle after \title ──
            const afterTitle = texFile.slice(titleEnd + 1);
            const shortTitleMatch = /^(\s*\\shorttitle\s*\{)/.exec(afterTitle);

            let fullOriginal, fullSuggestion;

            if (shortTitleMatch) {
                // \shorttitle exists — replace its content
                const shortStart = titleEnd + 1 +
                    afterTitle.indexOf(shortTitleMatch[0]);
                const shortContentStart = shortStart + shortTitleMatch[0].length;
                const { content: shortContent, end: shortEnd } =
                    extractBraceContent(texFile, shortContentStart);

                const originalShortTag = texFile.slice(shortStart, shortEnd + 1);
                const newShortTag = `\\shorttitle{${newShortTitleContent}}`;

                fullOriginal = originalTitle + texFile.slice(titleEnd + 1, shortEnd + 1);
                fullSuggestion = `${newTitleTag}\n${newShortTag}`;
            } else {
                // No \shorttitle — insert after \title
                fullOriginal = originalTitle;
                fullSuggestion = `${newTitleTag}\n\\shorttitle{${newShortTitleContent}}`;
            }

            // Skip if already correct
            if (fullOriginal.trim() === fullSuggestion.trim()) return staleIds;

            const position = getPositionFromIndex(texFile, titleStart, fullOriginal.length);

            if (isRejectedByLivePosition(position, this.name, fullOriginal)) return staleIds;

            const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
            if (overlapping.length > 0) {
                overlapping.forEach(old => staleIds.add(old.id));
            } else {
                if (isAlreadyAnchored(position, this.name, existingSuggestions)) return staleIds;
            }

            createAnchor(session, Range, position,
                fullOriginal, fullSuggestion,
                this.markerClass, this.name, this.description, anchorlist);

            return staleIds;
        }
    },

    //passed here
    TimeFormate: {
        name: "Time Format",
        description: "Convert AM/PM to a.m./p.m.",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/TimeFomattingBefore.png",
        afterImage: "/images/description/TimeFomattingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(?<![\\])\b(AM|PM|am|pm)\b/gm;
            const map = { am: "a.m.", pm: "p.m.", AM: "A.M.", PM: "P.M." };
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = map[fullMatch];
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    PercentageSymbol: {
        name: "Change Percentage to %",
        description: "Change Percentage words to %",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/PercentageBefore.png",
        afterImage: "/images/description/PercentageAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\b(\d+)\s*(percent|percentage)\b/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = fullMatch.replace(/\b(\d+)\s*(percent|percentage)\b/gi, "$1%");
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
    //no before and after
    HeadingTitleCase: {
        name: "Heading Title Case",
        description: "All headings should be in Title Case, capitalize after periods, preserve math and LaTeX commands",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const headingPattern =
                /\\(section|subsection|subsubsection|chapter|paragraph|subparagraph)\s*\*?\s*\{/g;
            let match;

            while ((match = headingPattern.exec(texFile)) !== null) {
                const commandType = match[1];
                const hasAsterisk = match[0].includes('*');
                const captionStartIndex = match.index + match[0].length - 1;
                const capturedBracedData = texFile.slice(captionStartIndex);
                const capturedText = getInsideTextofBraces(capturedBracedData);
                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);
                const finalContent = capitalizeInsideBraces(innerContent, true);
                if (finalContent === innerContent) continue;

                const commandPrefix = hasAsterisk ? `\\${commandType}*` : `\\${commandType}`;
                const fullMatch = `${commandPrefix}${capturedText}`;
                const suggestion = `${commandPrefix}{${finalContent}}`;
                const position = getPositionFromIndex(texFile, match.index, match[0].length - 1 +
                    capturedText.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
    //No Before and after
    FigureCaptions: {
        name: "Figure Caption Sentence Case",
        description: "Figure captions should be sentence case, capitalize after periods, preserve math and LaTeX commands, end with period",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const figurePattern = /\\begin\{figure\*?\}[\s\S]*?\\caption\s*\{/g;
            let match;

            while ((match = figurePattern.exec(texFile)) !== null) {
                const captionStartIndex = match.index + match[0].length - 1;
                const capturedBracedData = texFile.slice(captionStartIndex);
                const capturedText = getInsideTextofBraces(capturedBracedData);
                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);
                let finalContent = capitalizeInsideBraces(innerContent, true);
                const trimmed = finalContent.trim();
                if (!trimmed.endsWith('.')) finalContent = trimmed + '.';
                if (finalContent === innerContent) continue;

                const captionMatch = texFile.substring(match.index).match(/\\caption\s*\{/);
                const actualCaptionIndex = match.index + captionMatch.index;
                const fullMatch = `\\caption${capturedText}`;
                const suggestion = `\\caption{${finalContent}}`;
                const position = getPositionFromIndex(texFile, actualCaptionIndex, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
    //No before and after
    TableCaptions: {
        name: "Table Caption Sentence Case",
        description: "Table captions should be sentence case, capitalize after periods, preserve math and LaTeX commands, end with period",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const tablePattern = /\\begin\{table\*?\}[\s\S]*?\\caption\s*\{/g;
            let match;

            while ((match = tablePattern.exec(texFile)) !== null) {
                const captionStartIndex = match.index + match[0].length - 1;
                const capturedBracedData = texFile.slice(captionStartIndex);
                const capturedText = getInsideTextofBraces(capturedBracedData);
                if (!capturedText) continue;

                const innerContent = capturedText.slice(1, -1);
                let finalContent = capitalizeInsideBraces(innerContent, true);
                const trimmed = finalContent.trim();
                if (!trimmed.endsWith('.')) finalContent = trimmed + '.';
                if (finalContent === innerContent) continue;

                const captionMatch = texFile.substring(match.index).match(/\\caption\s*\{/);
                const actualCaptionIndex = match.index + captionMatch.index;
                const fullMatch = `\\caption${capturedText}`;
                const suggestion = `\\caption{${finalContent}}`;
                const position = getPositionFromIndex(texFile, actualCaptionIndex, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    EmDashSpacing: {
        name: "Em-dash Spacing",
        description: "No space before and after em-dash",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/EmdashingBefore.png",
        afterImage: "/images/description/EmdashingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\s+---\s+/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = '---';
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    SlashRemoveSpacing: {
        name: "Slash Remove Spacing",
        description: "No space before and after a slash",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/slashRemoveSpacingBefore.png",
        afterImage: "/images/description/slashRemoveSpacingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(\w+)\s+\/\s+(\w+)/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}/${match[2]}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    VersusUsage: {
        name: "Versus Usage",
        description: "Spell out 'versus' outside parenthesis, abbreviate 'vs.' inside",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/versusBefore.png",
        afterImage: "/images/description/versusAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const tryAnchor = (fullMatch, suggestion, position) => {
                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            };

            // "versus" inside parenthesis → "vs." 
            const pattern1 = /\([^)]*\bversus\b[^)]*\)/gi;
            let match;
            while ((match = pattern1.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = fullMatch.replace(/\bversus\b/gi, 'vs.');
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);
                tryAnchor(fullMatch, suggestion, position);
            }

            // "vs." outside parenthesis → "versus" 
            const pattern2 = /(?<!\()\b\w+\s+vs\.\s+\w+\b(?!\))/gi;
            while ((match = pattern2.exec(texFile)) !== null) {
                const before = texFile.substring(Math.max(0, match.index - 50), match.index);
                const openBefore = (before.match(/\(/g) || []).length;
                const closeBefore = (before.match(/\)/g) || []).length;
                if (openBefore !== closeBefore) continue;

                const fullMatch = match[0];
                const suggestion = fullMatch.replace(/\bvs\./gi, 'versus');
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);
                tryAnchor(fullMatch, suggestion, position);
            }

            return staleIds;
        }
    },
    RemoveDuplicateReferences: {
        name: "Remove Duplicate References",
        description: "Remove duplicate bibliography entries, keeping only the first occurrence",
        markerClass: "ace-duplicate-reference-highlight",
        beforeImage: "/images/description/RemoveDuplicateReferenceBefore.png",
        afterImage: "/images/description/RemoveDuplicateReferenceAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const bibitemRegex =
                /\\bibitem\{.*?\}%?\d*\s*([\s\S]*?)(?=\\bibitem|\\end\{thebibliography\}|\\AQ)/g;
            const seen = new Map();
            let match;

            while ((match = bibitemRegex.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const normalized = match[1].replace(/\s+/g, " ").replace(/[{}]/g, "").toLowerCase().trim();

                if (seen.has(normalized)) {
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, fullMatch, "",
                            this.markerClass, this.name,
                            "Duplicate reference detected. Remove this entry.", anchorlist);
                    }
                } else {
                    seen.set(normalized, match.index);
                }
            }

            return staleIds;
        }
    },

    TemperatureFormatting: {
        name: "Temperature Formatting",
        description: "Remove space before and after °",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/TemperatureFormatingBefore.png",
        afterImage: "/images/description/TemperatureFormatingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(\d+)\s*°\s*([A-Z]{1,2})/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}°${match[2]}`;
                if (fullMatch === suggestion) continue;

                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    SectionCallout: {
        name: "Section Callout Capitalization",
        description: "Capitalize 'Section' in section callouts",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/SectionCalloutBefore.png",
        afterImage: "/images/description/SectionCalloutAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\bsection\s*(~)?\\ref\{/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                if (!match[0].startsWith('section')) continue;
                const fullMatch = match[0];
                const suggestion = `Section~\\ref{`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    RepeatedWords: {
        name: "Delete Repeated Words",
        description: "Remove duplicate consecutive words",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/RepeatedWordsBefore.png",
        afterImage: "/images/description/RepeatedWordsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {
            const staleIds = new Set();
            const pattern = /\b(\w+)\s+\1\b/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]} \\AQ{Repeated word deleted}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) {
                    // ── Still advance by one word so next repeat is caught ──
                    pattern.lastIndex = match.index + match[1].length + 1;
                    continue;
                }

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) {
                        // ── Advance by one word for overlap detection too ──
                        pattern.lastIndex = match.index + match[1].length + 1;
                        continue;
                    }
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }


                pattern.lastIndex = match.index + match[1].length + 1;
            }

            return staleIds;
        }
    },

    QuotesStyling: {
        name: "Single to Double Quotes",
        description: "Change single quotes to double quotes (same line only)",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/SingletoDoubleBefore.png",
        afterImage: "/images/description/SingletoDoubleAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(?<!\`\`)`([^']+)'(?!')/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `\`\`${match[1]}''`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    LatinTermsItalic: {
        name: "Latin Terms Italic",
        description: "Latin terms should be italicized",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/Latexwordsbefore.png",
        afterImage: "/images/description/LatexwordsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const latinTerms = [
                'in vitro', 'in vivo', 'via', 'in situ', 'et al', 'etc', 'i.e.', 'e.g.',
                'cf.', 'vs.', 'vice versa', 'ad hoc', 'a priori', 'a posteriori',
                'de facto', 'de jure', 'per se', 'inter alia', 'ex post', 'ex ante'
            ];

            for (const term of latinTerms) {
                const escapedTerm = term.replace(/\./g, '\\.');
                const pattern = new RegExp(`(?<!\\\\textit\\{)${escapedTerm}(?![\\w}])`, 'gi');
                let match;

                while ((match = pattern.exec(texFile)) !== null) {
                    const fullMatch = match[0];
                    const suggestion = `\\textit{${fullMatch}}`;
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, fullMatch, suggestion,
                            this.markerClass, this.name, this.description, anchorlist);
                    }
                }
            }

            return staleIds;
        }
    },

    EquationCitation: {
        name: "Equation Citation",
        description: "Use \\eqref{} instead of (\\ref{})",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/EquationCitationBefore.png",
        afterImage: "/images/description/EquationCitationAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const tryAnchor = (fullMatch, suggestion, position) => {
                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            };

            const pattern1 = /Equation(~)?\s*\\ref\{([^}]+)\}/gi;
            let match;
            while ((match = pattern1.exec(texFile)) !== null) {
                const position = getPositionFromIndex(texFile, match.index, match[0].length);
                tryAnchor(match[0], `Equation~\\eqref{${match[2]}}`, position);
            }

            const pattern2 = /\(\\ref\{([^}]+)\}\)/g;
            while ((match = pattern2.exec(texFile)) !== null) {
                const position = getPositionFromIndex(texFile, match.index, match[0].length);
                tryAnchor(match[0], `\\eqref{${match[1]}}`, position);
            }

            return staleIds;
        }
    },


    LatexLabels: {
        name: "LaTeX Command Spacing",
        description: "Ensure proper spacing after LaTeX commands after \\cite{} or \\ref{} or \\label{} add a space",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/LatexCommandSpacingBefore.png",
        afterImage: "/images/description/LatexCommandSpacingAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            function isInsideParens(text, index) {
                const before = text.lastIndexOf('(', index);
                if (before === -1) return false;
                const after = text.indexOf(')', index);
                if (after === -1) return false;
                const closingBefore = text.indexOf(')', before);
                return closingBefore > index;
            }

            const pattern = /(Figure|Table)(~| )?\\ref\{[^}]+\}([a-zA-Z])(?![\w])/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                if (!isInsideParens(texFile, match.index)) continue;

                const fullMatch = match[0];
                const refMatch = fullMatch.match(/\\ref\{[^}]+\}/);
                if (!refMatch) continue;

                const abbrev = match[1] === "Figure" ? "Fig." : "Tab.";
                const suggestion = `${abbrev} ${refMatch[0]}(${match[3]})`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    RepeatingRanges: {
        name: "Format Ranges",
        description: "Format number ranges properly",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/NumberRangesBefore.png",
        afterImage: "/images/description/NumberRangesAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const rules = [
                {
                    pattern: /\((\d+)%?\s*[—-]\s*(\d+)\\?%\)/g, suggest: (m) =>
                        `(${m[1]}\\%--${m[2]}\\%)`
                },
                {
                    pattern: /\bfrom\s+(\d+)\s*\\?%?\s*[—-]\s*(\d+)\s*\\?%/gi, suggest: (m) => `from 
    ${m[1]}\\% to ${m[2]}\\%`
                },
                {
                    pattern: /\bbetween\s+(\d+)\s*\\?%?\s*[—-]\s*(\d+)\s*\\?%/gi, suggest: (m) => `between 
    ${m[1]}\\% and ${m[2]}\\%`
                },
            ];

            for (const { pattern, suggest } of rules) {
                pattern.lastIndex = 0;
                let match;
                while ((match = pattern.exec(texFile)) !== null) {
                    const fullMatch = match[0];
                    const suggestion = suggest(match);
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, fullMatch, suggestion,
                            this.markerClass, this.name, this.description, anchorlist);
                    }
                }
            }

            return staleIds;
        }
    },

    TimeAndDosageFormatting: {
        name: "Normalize Time/Dosage Ratios",
        description: "Convert unit ratios like second/day to sec/d",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/TimeandDosageBefore.png",
        afterImage: "/images/description/TimeandDosageAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const unitMap = {
                second: "sec", seconds: "sec", sec: "sec",
                minute: "min", minutes: "min", min: "min",
                hour: "h", hours: "h", h: "h",
                day: "d", days: "d", d: "d",
                milligram: "mg", milligrams: "mg", mg: "mg",
                gram: "g", grams: "g", g: "g"
            };
            const keys = Object.keys(unitMap).join("|");
            const pattern = new RegExp(`\\b(${keys})\\s*\\/\\s*(${keys})\\b`, "gi");
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion =
                    `${unitMap[match[1].toLowerCase()]}/${unitMap[match[2].toLowerCase()]}`;
                if (fullMatch === suggestion) continue;

                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    LatexCitations: {
        name: "LaTeX Citations",
        description: "Convert Equation, Table, and Figure references to LaTeX \\ref{} format",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/LatexCitationsBefore.png",
        afterImage: "/images/description/LatexCitationsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const rules = [
                {
                    pattern: /\b(Equation|Eq\.)\s*\((\d+)\)/gi, suggest: (m) => `${m[1]} 
    (\\ref{eqn${m[2]}})`
                },
                {
                    pattern: /\b(Table)\s+(\d+)\b/gi, suggest: (m) => `${m[1]} 
    \\ref{table-${m[2]}}`
                },
                {
                    pattern: /\b(Tables)\s+(\d+)\s+to\s+(\d+)\b/gi, suggest: (m) => `${m[1]} 
    \\ref{table-${m[2]}} to \\ref{table-${m[3]}}`
                },
                {
                    pattern: /\b(Figure|Fig\.)\s+(\d+)\b/gi, suggest: (m) => `${m[1]} \\ref{fig-${m[2]}}`
                },
            ];

            for (const { pattern, suggest } of rules) {
                pattern.lastIndex = 0;
                let match;
                while ((match = pattern.exec(texFile)) !== null) {
                    const fullMatch = match[0];
                    const suggestion = suggest(match);
                    const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, fullMatch, suggestion,
                            this.markerClass, this.name, this.description, anchorlist);
                    }
                }
            }

            return staleIds;
        }
    },

    //No before and after
    URLValidation: {
        name: "Invalid URL or DOI",
        description: "Validates URLs and DOIs, marks invalid ones",
        markerClass: "ace-Common-highlight",

        async execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const validateLink = async (link) => {
                try {
                    const response = await fetch(link, {
                        method: "HEAD", mode: "cors", redirect: "follow",
                        cache: "no-cache"
                    });
                    return response.status === 404
                        ? { valid: false, reason: "404 Not Found" }
                        : { valid: true };
                } catch (error) {
                    if (error.message.includes('CORS') || error.name === 'TypeError') return { valid: true };
                    return { valid: true };
                }
            };

            const tryAnchor = (fullMatch, suggestion, position, description) => {
                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, description, anchorlist);
                }
            };

            // Bare URLs 
            const bareUrlPattern = /(https?:\/\/[^\s\}\),]+)/gi;
            let match;
            const bareUrlMatches = [];
            while ((match = bareUrlPattern.exec(texFile)) !== null) {
                const before = texFile.substring(Math.max(0, match.index - 15), match.index);
                if (!before.includes('\\url{') && !before.includes('\\href{')) {
                    bareUrlMatches.push({ url: match[1], index: match.index, length: match[1].length });
                }
            }

            // Bare DOIs 
            const doiPattern = /(DOI:\s*10\.\d{4,9}\/[^\s\),]+)/gi;
            const doiMatches = [];
            while ((match = doiPattern.exec(texFile)) !== null) {
                const before = texFile.substring(Math.max(0, match.index - 50), match.index);
                const isInHref = before.includes('\\href{') &&
                    !before.substring(before.lastIndexOf('\\href{')).includes('}');
                if (!isInHref) {
                    doiMatches.push({ doi: match[1], index: match.index, length: match[1].length });
                }
            }

            // Wrapped URLs 
            const wrappedUrlPattern = /\\(?:url|href)\{(https?:\/\/[^\}]+)\}/gi;
            const wrappedMatches = [];
            while ((match = wrappedUrlPattern.exec(texFile)) !== null) {
                wrappedMatches.push({
                    fullMatch: match[0], url: match[1], index: match.index, length:
                        match[0].length
                });
            }

            for (const m of bareUrlMatches) {
                const result = await validateLink(m.url);
                const position = getPositionFromIndex(texFile, m.index, m.length);
                const desc = result.valid ? "Bare URL should be wrapped in \\url{}" : `Invalid URL 
    (${result.reason})`;
                tryAnchor(m.url, `\\url{${m.url}}`, position, desc);
            }

            for (const m of doiMatches) {
                const doiNumber = m.doi.replace(/^DOI:\s*/i, '').trim();
                const doiUrl = `https://doi.org/${doiNumber}`;
                const result = await validateLink(doiUrl);
                const position = getPositionFromIndex(texFile, m.index, m.length);
                const desc = result.valid ? "Convert DOI to clickable link" : `Invalid DOI 
    (${result.reason})`;
                tryAnchor(m.doi, `\\href{${doiUrl}}{${m.doi}}`, position, desc);
            }

            for (const m of wrappedMatches) {
                const result = await validateLink(m.url);
                if (!result.valid) {
                    const position = getPositionFromIndex(texFile, m.index, m.length);
                    tryAnchor(m.fullMatch, '', position, `URL returns ${result.reason} - check if URL is 
    correct`);
                }
            }

            return staleIds;
        }
    },

    AddSpacing: {
        name: "Adding Space Around Symbols",
        description: "Add space around symbols only if numbers (any length) or single-letter strings are on both sides",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/AddSpacingAroundSymbolsBefore.png",
        afterImage: "/images/description/AddSpacingAroundSymbolsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /([A-Za-z0-9]+)([\+±<>\/\*\-\=])([A-Za-z0-9]+)/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const [fullMatch, left, symbol, right] = match;
                pattern.lastIndex = match.index + 1;

                const leftValid = /^\d+$/.test(left) || /^[A-Za-z]$/.test(left);
                const rightValid = /^\d+$/.test(right) || /^[A-Za-z]$/.test(right);
                if (!leftValid || !rightValid) continue;

                const suggestion = `${left} ${symbol} ${right}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    RemoveSpacing: {
        name: "Removing Space Around Symbols",
        description: "Remove extra spaces around symbols where numbers (any length) or single-letter strings are on both sides",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/RemoveSpacingAroundSymbolsBefore.png",
        afterImage: "/images/description/RemoveSpacingAroundSymbolsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /([A-Za-z0-9]+)\s+([\+±<>\/\*\-\=])\s+([A-Za-z0-9]+)/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const [fullMatch, left, symbol, right] = match;
                pattern.lastIndex = match.index + 1;

                const leftValid = /^\d+$/.test(left) || /^[A-Za-z]$/.test(left);
                const rightValid = /^\d+$/.test(right) || /^[A-Za-z]$/.test(right);
                if (!leftValid || !rightValid) continue;

                const suggestion = `${left}${symbol}${right}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    // NegativeValues: {
    //     name: "Normalize Negative Values & Fractions",
    //     description: "Standardizes negative numbers, numeric and word fractions, axis variables, and degree/prime symbols for consistent LaTeX formatting",
    //     markerClass: "ace-Common-highlight",
    //     beforeImage: "/images/description/NegativeValuesBefore.png",
    //     afterImage: "/images/description/NegativeValuesAfter.png",

    //     execute(texFile, session, Range, anchorlist,
    //         fileName, Allfiles,
    //         existingSuggestions = [],
    //         rejectedSuggestions = []) {

    //         const staleIds = new Set();
    //         const italicMap = { x: '𝑥', y: '𝑦', z: '𝑧', X: '𝑋', Y: '𝑌', Z: '𝑍' };
    //         const patterns = [
    //             { regex: /(^|\s)[−](\d+)/g, replacer: (m, g1, g2) => `${g1}-${g2}` },
    //             { regex: /(^|\s)(\d+)\s*\/\s*(\d+)/g, replacer: (m, g1, g2, g3) => `${g1}${g2}/${g3}` },
    //             { regex: /\b(one|two|three|four|five|six|seven|eight|nine|ten)\s+(half|halves|thirds?|quarters?|fourths?|fifths?|sixths?|sevenths?|eighths?|ninths?|tenths?)\b/gi, replacer: (m, g1, g2) => `${g1}-${g2}` },
    //             {
    //                 regex: /([A-Z]{2})\s*°\s*([A-Z]{2})\s*['']\s*([A-Z])\s*[""]/g, replacer: (m, g1, g2, g3) =>
    //                     `${g1}°${g2}′${g3}″`
    //             },
    //             { regex: /(\w)\s*-?\s*axis/g, replacer: (m, g1) => `${italicMap[g1] || g1}-axis` },
    //         ];

    //         for (const { regex, replacer } of patterns) {
    //             regex.lastIndex = 0;
    //             let match;
    //             while ((match = regex.exec(texFile)) !== null) {
    //                 const fullMatch = match[0];
    //                 const suggestion = replacer(...match);
    //                 if (fullMatch === suggestion) continue;

    //                 const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

    //                 if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

    //                 const overlapping = getOverlappingAnchors(position, this.name,
    //                     existingSuggestions);
    //                 if (overlapping.length > 0) {
    //                     overlapping.forEach(old => staleIds.add(old.id));
    //                 } else {
    //                     if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
    //                 }

    //                 if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
    //                     createAnchor(session, Range, position, fullMatch, suggestion,
    //                         this.markerClass, this.name, this.description, anchorlist);
    //                 }
    //             }
    //         }

    //         return staleIds;
    //     }
    // },


    dashclassification: {
        name: "Normalize Dash Between Numbers",
        description: "Change dash, en-dash, em-dash with spaces to a single '-' between numbers",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/dashclassificationBefore.png",
        afterImage: "/images/description/dashclassificationAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(\d+)\s+[-–—]\s+(\d+)/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `${match[1]}-${match[2]}`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    ConvertNumber: {
        name: "Convert number to Text upto 10",
        description: "Convert number to text words",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/ConvertNumberBefore.png",
        afterImage: "/images/description/ConvertNumberAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            function getMathRanges(text) {
                const ranges = [];
                const mathEnvs = [
                    'equation', 'equation\\*', 'align', 'align\\*',
                    'alignat', 'alignat\\*', 'gather', 'gather\\*',
                    'multline', 'multline\\*', 'flalign', 'flalign\\*',
                    'eqnarray', 'eqnarray\\*', 'array', 'split',
                    'cases', 'dcases', 'math', 'displaymath'
                ];
                mathEnvs.forEach(env => {
                    const regex = new RegExp(`\\\\begin\\{${env}\\}[\\s\\S]*?\\\\end\\{${env}\\}`, 'g');
                    let m;
                    while ((m = regex.exec(text)) !== null) ranges.push([m.index, m.index + m[0].length]);
                });
                let m;
                const displayMath = /\\\[[\s\S]*?\\\]/g;
                while ((m = displayMath.exec(text)) !== null) ranges.push([m.index, m.index +
                    m[0].length]);
                const inlineMath = /(?<!\$)\$(?!\$)[\s\S]*?(?<!\$)\$(?!\$)/g;
                while ((m = inlineMath.exec(text)) !== null) ranges.push([m.index, m.index +
                    m[0].length]);
                const displayDollar = /\$\$[\s\S]*?\$\$/g;
                while ((m = displayDollar.exec(text)) !== null) ranges.push([m.index, m.index +
                    m[0].length]);
                const inlineParen = /\\\([\s\S]*?\\\)/g;
                while ((m = inlineParen.exec(text)) !== null) ranges.push([m.index, m.index +
                    m[0].length]);
                return ranges;
            }

            const mathRanges = getMathRanges(texFile);
            const numbers = {
                1: "one", 2: "two", 3: "three", 4: "four", 5: "five",
                6: "six", 7: "seven", 8: "eight", 9: "nine", 10: "ten"
            };
            const regex = /(?<=^|\s)(10|[1-9])(?=\s|$)/gmi;
            let match;

            while ((match = regex.exec(texFile)) !== null) {
                if (mathRanges.some(([s, e]) => match.index >= s && match.index < e)) continue;

                const suggestion = numbers[parseInt(match[1])];
                if (!suggestion) continue;

                const fullMatch = match[1];
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    cdotsLdotsToggle: {
        name: "\\cdots <-> \\ldots toggle",
        description: "Change \\cdots to \\ldots and \\ldots to \\cdots",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/cdotsLdotsToggleBefore.png",
        afterImage: "/images/description/cdotsLdotsToggleAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /(\\cdots|\\ldots)/gmi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = fullMatch.toLowerCase() === '\\cdots' ? '\\ldots' : '\\cdots';
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },

    UndefinedReferences: {
        name: "Undefined References",
        description: "Detect \\ref, \\eqref, and \\cite with empty or undefined labels",
        markerClass: "ace-undefined-ref",
        beforeImage: "/images/description/UndefinedReferencesBefore.png",
        afterImage: "/images/description/UndefinedReferencesAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            // Collect defined labels and bibitems 
            const definedLabels = new Set();
            let match;
            const labelPattern = /\\label\{([^}]+)\}/g;
            while ((match = labelPattern.exec(texFile)) !== null) {
                if (match[1].trim()) definedLabels.add(match[1].trim());
            }
            const bibitemPattern = /\\bibitem\{([^}]+)\}/g;
            while ((match = bibitemPattern.exec(texFile)) !== null) {
                if (match[1].trim()) definedLabels.add(match[1].trim());
            }

            function findReferences(text, command) {
                const results = [];
                const regex = new RegExp(`\\\\${command}\\{`, 'g');
                let m;
                while ((m = regex.exec(text)) !== null) {
                    const refStart = m.index;
                    let i = m.index + m[0].length;
                    let depth = 1;
                    while (i < text.length && depth > 0) {
                        if (text[i] === '\\') { i += 2; continue; }
                        if (text[i] === '{') depth++;
                        else if (text[i] === '}') depth--;
                        i++;
                    }
                    if (depth === 0) {
                        results.push({
                            start: refStart,
                            fullMatch: text.slice(refStart, i),
                            labelContent: text.slice(m.index + m[0].length, i - 1).trim(),
                            command
                        });
                    }
                }
                return results;
            }

            const tryAnchor = (fullMatch, position, description) => {
                if (isRejectedByLivePosition(position, this.name, fullMatch)) return;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                }
                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, '',
                        this.markerClass, this.name, description, anchorlist);
                }
            };

            for (const cmd of ['ref', 'eqref']) {
                for (const { start, fullMatch, labelContent, command } of findReferences(texFile, cmd)) {
                    const position = getPositionFromIndex(texFile, start, fullMatch.length);
                    if (!labelContent) {
                        tryAnchor(fullMatch, position, `Empty \\${command}: label is missing`);
                    } else if (!definedLabels.has(labelContent)) {
                        tryAnchor(fullMatch, position, `Undefined \\${command}: label "${labelContent}" not 
    found`);
                    }
                }
            }

            for (const { start, fullMatch, labelContent, command } of findReferences(texFile, 'cite')) {
                const position = getPositionFromIndex(texFile, start, fullMatch.length);
                if (!labelContent) {
                    tryAnchor(fullMatch, position, `Empty \\${command}: citation key is missing`);
                } else {
                    const undefined_ = labelContent.split(',').map(l => l.trim()).filter(l => l &&
                        !definedLabels.has(l));
                    if (undefined_.length > 0) {
                        const desc = undefined_.length === 1
                            ? `Undefined \\${command}: citation key "${undefined_[0]}" not found`
                            : `Undefined \\${command}: citation keys not found: ${undefined_.join(', ')}`;
                        tryAnchor(fullMatch, position, desc);
                    }
                }
            }

            return staleIds;
        }
    },

    DuplicateLabels: {
        name: "Duplicate Labels",
        description: "Detect duplicate \\label definitions",
        markerClass: "ace-duplicate-label",
        beforeImage: "/images/description/DuplicateLabelsBefore.png",
        afterImage: "/images/description/DuplicateLabelsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            function findLabels(text) {
                const results = [];
                const regex = /\\label\{/g;
                let m;
                while ((m = regex.exec(text)) !== null) {
                    if (LineCommented(texFile, m.index)) continue;
                    let i = m.index + m[0].length;
                    let depth = 1;
                    while (i < text.length && depth > 0) {
                        if (text[i] === '\\') { i += 2; continue; }
                        if (text[i] === '{') depth++;
                        else if (text[i] === '}') depth--;
                        i++;
                    }
                    if (depth === 0) {
                        const labelName = text.slice(m.index + m[0].length, i - 1).trim();
                        if (labelName) results.push({
                            start: m.index, fullMatch: text.slice(m.index, i),
                            labelName
                        });
                    }
                }
                return results;
            }

            const labelOccurrences = {};
            for (const label of findLabels(texFile)) {
                if (!labelOccurrences[label.labelName]) labelOccurrences[label.labelName] = [];
                labelOccurrences[label.labelName].push(label);
            }

            for (const [labelName, occurrences] of Object.entries(labelOccurrences)) {
                if (occurrences.length <= 1) continue;
                occurrences.forEach((occ, index) => {
                    const position = getPositionFromIndex(texFile, occ.start, occ.fullMatch.length);
                    const description = index === 0
                        ? `Duplicate label "${labelName}" (first occurrence, ${occurrences.length - 1} more 
    found)`
                        : `Duplicate label "${labelName}" (occurrence ${index + 1} of 
    ${occurrences.length})`;

                    if (isRejectedByLivePosition(position, this.name, occ.fullMatch)) return;
                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                    }
                    if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                        createAnchor(session, Range, position, occ.fullMatch, '',
                            this.markerClass, this.name, description, anchorlist);
                    }
                });
            }

            return staleIds;
        }
    },

    UnusedLabels: {
        name: "Unused Labels",
        description: "Detect \\label definitions that are never referenced",
        markerClass: "ace-unused-label",
        beforeImage: "/images/description/UnusedLabelsBefore.png",
        afterImage: "/images/description/UnusedLabelsAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            function findLabels(text) {
                const results = [];
                const regex = /\\label\{/g;
                let m;
                while ((m = regex.exec(text)) !== null) {
                    if (LineCommented(texFile, m.index)) continue;
                    let i = m.index + m[0].length;
                    let depth = 1;
                    while (i < text.length && depth > 0) {
                        if (text[i] === '\\') { i += 2; continue; }
                        if (text[i] === '{') depth++;
                        else if (text[i] === '}') depth--;
                        i++;
                    }
                    if (depth === 0) {
                        const labelName = text.slice(m.index + m[0].length, i - 1).trim();
                        if (labelName) results.push({
                            start: m.index, fullMatch: text.slice(m.index, i),
                            labelName
                        });
                    }
                }
                return results;
            }

            function collectRefs(text, command, referencedLabels) {
                const regex = new RegExp(`\\\\${command}\\{`, 'g');
                let m;
                while ((m = regex.exec(text)) !== null) {
                    let i = m.index + m[0].length;
                    let depth = 1;
                    while (i < text.length && depth > 0) {
                        if (text[i] === '\\') { i += 2; continue; }
                        if (text[i] === '{') depth++;
                        else if (text[i] === '}') depth--;
                        i++;
                    }
                    if (depth === 0) {
                        const content = text.slice(m.index + m[0].length, i - 1).trim();
                        if (content) {
                            if (command === 'cite') {
                                content.split(',').map(l => l.trim()).filter(Boolean).forEach(l =>
                                    referencedLabels.add(l));
                            } else {
                                referencedLabels.add(content);
                            }
                        }
                    }
                }
            }

            const definedLabels = {};
            for (const label of findLabels(texFile)) {
                if (!definedLabels[label.labelName]) definedLabels[label.labelName] = [];
                definedLabels[label.labelName].push({ start: label.start, fullMatch: label.fullMatch });
            }

            const referencedLabels = new Set();
            for (const cmd of ['ref', 'eqref', 'cite', 'pageref', 'autoref', 'nameref', 'cref', 'Cref']) {
                collectRefs(texFile, cmd, referencedLabels);
            }

            for (const [labelName, occurrences] of Object.entries(definedLabels)) {
                if (referencedLabels.has(labelName)) continue;
                occurrences.forEach((labelInfo, index) => {
                    const position = getPositionFromIndex(texFile, labelInfo.start,
                        labelInfo.fullMatch.length);
                    const description = occurrences.length > 1
                        ? `Unused label "${labelName}" (occurrence ${index + 1} of ${occurrences.length}) - never referenced`
                        : `Unused label "${labelName}" - never referenced in document`;

                    if (isRejectedByLivePosition(position, this.name, labelInfo.fullMatch)) return;
                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) return;
                    }
                    createAnchor(session, Range, position, labelInfo.fullMatch, '',
                        this.markerClass, this.name, description, anchorlist);
                });
            }

            return staleIds;
        }
    },
    //no before and after
    NonSequentialReferences: {
        name: "Non-Sequential References",
        description: "Detect figures/tables referenced out of sequential order",
        markerClass: "ace-non-sequential-ref",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const isCommented = (text, index) => {
                let lineStart = index;
                while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart--;
                const linePrefix = text.slice(lineStart, index);
                for (let i = 0; i < linePrefix.length; i++) {
                    if (linePrefix[i] === '%' && (i === 0 || linePrefix[i - 1] !== '\\')) return true;
                }
                return false;
            };

            const labelInfo = {};
            let labelCounter = 0;
            const envRegex = /\\begin\{(table|figure)\*?\}([\s\S]*?)\\end\{\1\*?\}/g;
            let envMatch;

            while ((envMatch = envRegex.exec(texFile)) !== null) {
                const envType = envMatch[1];
                const envContent = envMatch[2];
                const envStartIndex = envMatch.index + envMatch[0].indexOf(envMatch[2]);
                const labelRegex = /\\label\{/g;
                let labelMatch;

                while ((labelMatch = labelRegex.exec(envContent)) !== null) {
                    const absoluteIndex = envStartIndex + labelMatch.index;
                    if (isCommented(texFile, absoluteIndex)) continue;

                    let i = labelMatch.index + labelMatch[0].length;
                    let depth = 1;
                    while (i < envContent.length && depth > 0) {
                        if (envContent[i] === '{') depth++;
                        else if (envContent[i] === '}') depth--;
                        i++;
                    }
                    if (depth === 0) {
                        const labelName = envContent.slice(labelMatch.index + labelMatch[0].length, i -
                            1).trim();
                        if (labelName && !labelInfo.hasOwnProperty(labelName)) {
                            labelInfo[labelName] = { order: labelCounter++, type: envType };
                        }
                    }
                }
            }

            const lastSeenOrder = { figure: -1, table: -1 };
            const refRegex = /\\ref\{([^}]+)\}/g;
            let refMatch;

            while ((refMatch = refRegex.exec(texFile)) !== null) {
                const refLabel = refMatch[1].trim();
                const fullMatch = refMatch[0];
                if (isCommented(texFile, refMatch.index)) continue;
                if (!labelInfo.hasOwnProperty(refLabel)) continue;

                const { order: currentOrder, type: refType } = labelInfo[refLabel];
                const lastOrder = lastSeenOrder[refType];

                if (currentOrder < lastOrder) {
                    const prevLabelName = Object.entries(labelInfo).find(
                        ([, info]) => info.type === refType && info.order === lastOrder
                    )?.[0] ?? 'unknown';
                    const typeLabel = refType === 'figure' ? 'Figure' : 'Table';
                    const description = `${typeLabel} "${refLabel}" is cited out of sequential order. 
    Previously cited "${prevLabelName}" which appears later in the document.`;
                    const position = getPositionFromIndex(texFile, refMatch.index, fullMatch.length);

                    if (isRejectedByLivePosition(position, this.name, fullMatch)) {
                        lastSeenOrder[refType] = Math.max(lastOrder, currentOrder);
                        continue;
                    }
                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) {
                            lastSeenOrder[refType] = Math.max(lastOrder, currentOrder);
                            continue;
                        }
                    }
                    createAnchor(session, Range, position, fullMatch, '',
                        this.markerClass, this.name, description, anchorlist);
                }

                lastSeenOrder[refType] = Math.max(lastOrder, currentOrder);
            }

            return staleIds;
        }
    },

    //no before and after
    MathCommaFix: {
        name: "Math Comma Fix",
        description: "Move comma outside of inline math expressions like $abc,$ → $abc$,",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const regex = /\$(.+?),\$/g;
            let match;

            while ((match = regex.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `$${match[1]}$,`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, "Move comma outside of inline math", anchorlist);
                }
            }

            return staleIds;
        }
    },

    FigureRef: {
        name: "Convert FigureRef",
        description: "Convert Figure \\ref{fig1}a to Figure \\ref{fig1}(a)",
        markerClass: "ace-Common-highlight",


        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const regex = /Figure\s*\\ref\{([^}]*)\}([\w])(?!\w)/gi;
            let match;

            while ((match = regex.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const suggestion = `Figure \\ref{${match[1]}}(${match[2]})`;
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, "Figure Ref Rechange", anchorlist);
                }
            }

            return staleIds;
        }
    },
    //no before and after
    UnusedReferencefinding: {
        name: "unused reference finding",
        description: "finding unused references",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const labels = new Set();
            let match;

            const labelPattern = /\\label\{([^}]*)\}/gi;
            while ((match = labelPattern.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;
                labels.add(match[1].trim());
            }

            const refPattern = /\\ref\{([^}]*)\}/gi;
            while ((match = refPattern.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;
                const refKey = match[1].trim();
                if (labels.has(refKey)) continue;

                const position = getPositionFromIndex(texFile, match.index, match[0].length);

                if (isRejectedByLivePosition(position, this.name, match[0])) continue;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                const prevline = position.startRow > 0 ? session.getLine(position.startRow - 1) || "" : "";
                if (!prevline.trim().startsWith("\\AQ{")) {
                    anchorlist.push({
                        id: `${this.name}__${crypto.randomUUID()}`,
                        startAnchor: session.doc.createAnchor(position.startRow, position.startCol),
                        endAnchor: session.doc.createAnchor(position.endRow, position.endCol),
                        markerId: null,
                        originalText: match[1],
                        suggestionText: null,
                        queryOnly: true,
                        styleInfo: {
                            name: this.name, description: `reference ${refKey} has no matching 
    label` }
                    });
                }
            }

            return staleIds;
        }
    },
    //no before and after
    unusedLabelsFinding: {
        name: "unused labels finding",
        description: "finding unused labels",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const refs = new Set();
            let match;

            const refPattern = /\\ref\{([^}]*)\}/gi;
            while ((match = refPattern.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;
                refs.add(match[1].trim());
            }

            const labelPattern = /\\label\{([^}]*)\}/gi;
            while ((match = labelPattern.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;
                const refKey = match[1].trim();
                if (refs.has(refKey)) continue;

                const position = getPositionFromIndex(texFile, match.index, match[0].length);

                if (isRejectedByLivePosition(position, this.name, match[0])) continue;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                const prevline = position.startRow > 0 ? session.getLine(position.startRow - 1) || "" : "";
                if (!prevline.trim().startsWith("\\AQ{")) {
                    anchorlist.push({
                        id: `${this.name}__${crypto.randomUUID()}`,
                        startAnchor: session.doc.createAnchor(position.startRow, position.startCol),
                        endAnchor: session.doc.createAnchor(position.endRow, position.endCol),
                        markerId: null,
                        originalText: match[1],
                        suggestionText: null,
                        queryOnly: true,
                        styleInfo: {
                            name: this.name, description: `label ${refKey} has no matching 
    reference` }
                    });
                }
            }

            return staleIds;
        }
    },
    //no before and after
    unusedCite: {
        name: "unused cite finding",
        description: "finding unused cite",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            if (!Allfiles || !texFile) return new Set();
            const staleIds = new Set();

            const allCites = new Set();
            const citePattern = /\\cite\s*\{([^}]*)\}/g;
            let match;

            const files = Array.isArray(Allfiles) ? Allfiles : (Allfiles?.data || []);
            files.filter(item => item.content !== texFile).forEach(item => {
                const content = item.content || "";
                citePattern.lastIndex = 0;
                while ((match = citePattern.exec(content)) !== null) {
                    if (isCommented(content, match.index)) continue;
                    match[1].split(",").forEach(k => allCites.add(k.trim()));
                }
            });

            const patternbib = /\\bibitem\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/gi;
            while ((match = patternbib.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;
                const key = match[1].trim();
                if (allCites.has(key)) continue;

                const position = getPositionFromIndex(texFile, match.index, match[0].length);

                if (isRejectedByLivePosition(position, this.name, match[0])) continue;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                const prevline = position.startRow > 0 ? session.getLine(position.startRow - 1) || "" : "";
                if (!prevline.trim().startsWith("\\AQ{")) {
                    anchorlist.push({
                        id: `${this.name}__${crypto.randomUUID()}`,
                        startAnchor: session.doc.createAnchor(position.startRow, position.startCol),
                        endAnchor: session.doc.createAnchor(position.endRow, position.endCol),
                        markerId: null,
                        originalText: key,
                        suggestionText: null,
                        queryOnly: true,
                        styleInfo: { name: this.name, description: `this bibitem is not cited anywhere` }
                    });
                }
            }

            return staleIds;
        }
    },
    //no before and after
    authornamecheck: {
        name: "Author Name Checking",
        description: "A query to check the author name is correctly formatted or not",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\\author\{/gi;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;

                const position = getPositionFromIndex(texFile, match.index, match[0].length);

                if (isRejectedByLivePosition(position, this.name, match[0])) continue;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                const prevLine = position.startRow > 0 ? session.getLine(position.startRow - 1) || "" : "";
                if (!prevLine.trim().startsWith("\\AQ")) {
                    anchorlist.push({
                        id: `${this.name}__${crypto.randomUUID()}`,
                        startAnchor: session.doc.createAnchor(position.startRow, position.startCol),
                        endAnchor: session.doc.createAnchor(position.endRow, position.endCol),
                        markerId: null,
                        originalText: null,
                        suggestionText: null,
                        queryOnly: true,
                        styleInfo: {
                            name: "Author name checking",
                            description: `Please check all author names, initials, and affiliations to confirm 
    they are accurate.`
                        }
                    });
                }
            }

            return staleIds;
        }
    },

    EquationEndCapitalization: {
        name: "Equation End Capitalization",
        description: "Ensure the first letter after \\end{equation} is capitalized",
        markerClass: "ace-Common-highlight",
        beforeImage: "/images/description/EndEquationBefore.png",
        afterImage: "/images/description/EndEquationAfter.png",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();
            const pattern = /\\end\{equation\}\s*\n\s*(?:\\\w+\{)?([a-zA-Z])/g;
            let match;

            while ((match = pattern.exec(texFile)) !== null) {
                const fullMatch = match[0];
                const firstLetter = match[1];
                if (firstLetter === firstLetter.toUpperCase()) continue;

                const suggestion = fullMatch.replace(firstLetter, firstLetter.toUpperCase());
                const position = getPositionFromIndex(texFile, match.index, fullMatch.length);

                if (isRejectedByLivePosition(position, this.name, fullMatch)) continue;

                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }

                if (!findCommandorNot(position.startRow, position.startCol, texFile)) {
                    createAnchor(session, Range, position, fullMatch, suggestion,
                        this.markerClass, this.name, this.description, anchorlist);
                }
            }

            return staleIds;
        }
    },
    //no before and after
    unusedbib: {
        name: "unused bibitem finding",
        description: "finding unused bibitem",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            if (!Allfiles || !texFile) return new Set();
            const staleIds = new Set();

            const allBibs = new Set();
            const bibPattern = /\\bibitem\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/g;
            let match;

            const files = Array.isArray(Allfiles) ? Allfiles : (Allfiles?.data || []);
            files.forEach(item => {
                const content = item.content || "";
                bibPattern.lastIndex = 0;
                while ((match = bibPattern.exec(content)) !== null) {
                    if (isCommented(content, match.index)) continue;
                    allBibs.add(match[1].trim());
                }
            });

            const citePattern = /\\cite\s*\{([^}]*)\}/gi;
            while ((match = citePattern.exec(texFile)) !== null) {
                if (isCommented(texFile, match.index)) continue;
                const keys = match[1].split(",").map(k => k.trim());

                for (const key of keys) {
                    if (!key || allBibs.has(key)) continue;

                    const position = getPositionFromIndex(texFile, match.index, match[0].length);

                    if (isRejectedByLivePosition(position, this.name, match[0])) continue;
                    const overlapping = getOverlappingAnchors(position, this.name,
                        existingSuggestions);
                    if (overlapping.length > 0) {
                        overlapping.forEach(old => staleIds.add(old.id));
                    } else {
                        if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                    }

                    const prevLine = position.startRow > 0 ? session.getLine(position.startRow - 1) || "" :
                        "";
                    if (!prevLine.trim().startsWith("\\AQ")) {
                        anchorlist.push({
                            id: `${this.name}__${crypto.randomUUID()}`,
                            startAnchor: session.doc.createAnchor(position.startRow, position.startCol),
                            endAnchor: session.doc.createAnchor(position.endRow, position.endCol),
                            markerId: null,
                            originalText: key,
                            suggestionText: null,
                            queryOnly: true,
                            styleInfo: {
                                name: this.name, description: `\\cite{${key}} has no matching 
    \\bibitem` }
                        });
                    }
                }
            }

            return staleIds;
        }
    },
    //no before and after
    TableNumericAlignment: {
        name: "Table Numeric Alignment",
        description: "Apply right/decimal align to numeric table columns",
        markerClass: "ace-Common-highlight",

        execute(texFile, session, Range, anchorlist,
            fileName, Allfiles,
            existingSuggestions = [],
            rejectedSuggestions = []) {

            const staleIds = new Set();

            const isInsideCommentedTable = (text, tabularStart) => {
                const beforeTabular = text.slice(0, tabularStart);
                const tableBeginPattern = /\\begin\{table\*?\}/g;
                let lastTableBegin = null, match;
                while ((match = tableBeginPattern.exec(beforeTabular)) !== null) {
                    lastTableBegin = { index: match.index, text: match[0] };
                }
                if (!lastTableBegin) return false;
                const betweenText = text.slice(lastTableBegin.index, tabularStart);
                if (/\\end\{table\*?\}/.test(betweenText)) return false;
                if (isLineCommented(text, lastTableBegin.index)) return true;
                const afterTabular = text.slice(tabularStart);
                const endTableMatch = afterTabular.match(/\\end\{table\*?\}/);
                if (endTableMatch && isLineCommented(text, tabularStart + endTableMatch.index))
                    return true;
                return false;
            };

            const isNumericCell = (cell) => {
                let clean = cell
                    .replace(/\\hline/g, '').replace(/\\cline\{[^}]*\}/g, '')
                    .replace(/\\multicolumn\{[^}]*\}\{[^}]*\}\{([^}]*)\}/g, '$1')
                    .replace(/\\textbf\{([^}]*)\}/g, '$1').replace(/\\textit\{([^}]*)\}/g, '$1')
                    .replace(/\\text\{([^}]*)\}/g, '$1').replace(/\$([^$]*)\$/g, '$1')
                    .replace(/\\[a-zA-Z]+\s*/g, '').replace(/[{}]/g, '').replace(/\s+/g, '').trim();

                if (clean === '' || clean === '-' || clean === '--' || clean === '---')
                    return { skip: true, isNumeric: false, hasDecimal: false };

                const isInteger = /^[+-]?\d+$/.test(clean);
                const isDecimal = /^[+-]?\d+\.\d+$/.test(clean);
                const isScientific = /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/.test(clean);
                const isNumeric = isInteger || isDecimal || isScientific;
                return { skip: false, isNumeric, hasDecimal: isDecimal || isScientific };
            };

            const tabularEnvs = findTabularEnvs(texFile);

            for (const { start, fullMatch, envName, columnSpec, tableBody } of tabularEnvs) {
                if (isInsideCommentedTable(texFile, start)) continue;

                const endPattern = new RegExp(`\\\\end\\{${envName}\\*?\\}`);
                const endMatch = fullMatch.match(endPattern);
                if (!endMatch) continue;

                const endIndex = start + fullMatch.lastIndexOf(endMatch[0]);
                if (isLineCommented(texFile, start) || isLineCommented(texFile, endIndex)) continue;

                const specParts = parseColumnSpec(columnSpec);
                const dataColIndices = specParts.map((p, idx) => p.isDataCol ? idx : -1).filter(idx => idx
                    !== -1);
                if (dataColIndices.length === 0) continue;

                const numDataCols = dataColIndices.length;
                const rawRows = tableBody.split(/\\\\(?:\[[^\]]*\])?/);
                const parsedRows = rawRows
                    .map(row => {
                        const clean = row
                            .replace(/\\hline|\\cline\{[^}]*\}|\\toprule|\\midrule|\\bottomrule/g, '')
                            .trim();
                        return clean ? clean.split('&').map(c => c.trim()) : null;
                    })
                    .filter(row => row !== null && row.length > 1);

                if (parsedRows.length === 0) continue;

                const startRow = parsedRows[0].every(cell => {
                    const { isNumeric, skip } = isNumericCell(cell);
                    return !isNumeric && !skip;
                }) ? 1 : 0;

                const colStats = Array.from({ length: numDataCols }, () => ({
                    totalCells: 0, numericCells:
                        0, decimalCells: 0
                }));

                for (let rowIdx = startRow; rowIdx < parsedRows.length; rowIdx++) {
                    parsedRows[rowIdx].forEach((cell, cellIdx) => {
                        if (cellIdx >= numDataCols) return;
                        const result = isNumericCell(cell);
                        if (!result.skip) {
                            colStats[cellIdx].totalCells++;
                            if (result.isNumeric) {
                                colStats[cellIdx].numericCells++;
                                if (result.hasDecimal) colStats[cellIdx].decimalCells++;
                            }
                        }
                    });
                }

                const newSpecParts = specParts.map(p => ({ ...p }));
                let needsChange = false;

                dataColIndices.forEach((specIdx, dataColPos) => {
                    const stats = colStats[dataColPos];
                    if (stats.totalCells === 0 || stats.numericCells !== stats.totalCells) return;
                    const currentAlign = specParts[specIdx].align || specParts[specIdx].char;
                    const targetAlign = stats.decimalCells > 0 ? 'S' : 'r';
                    if (currentAlign !== targetAlign) {
                        newSpecParts[specIdx].char = targetAlign;
                        needsChange = true;
                    }
                });

                if (!needsChange) continue;

                const newColumnSpec = newSpecParts.map(p => p.char).join('');

                // Rejection / duplicate check on the column spec position 
                const specIndex = texFile.indexOf(columnSpec, start);
                if (specIndex === -1) continue;
                const position = getPositionFromIndex(texFile, specIndex, columnSpec.length);

                if (isRejectedByLivePosition(position, this.name, columnSpec)) continue;
                const overlapping = getOverlappingAnchors(position, this.name, existingSuggestions);
                if (overlapping.length > 0) {
                    overlapping.forEach(old => staleIds.add(old.id));
                } else {
                    if (isAlreadyAnchored(position, this.name, existingSuggestions)) continue;
                }
                createGranularAnchors(session, Range, texFile, start, fullMatch,
                    columnSpec, newColumnSpec, this.markerClass, this.name, anchorlist);
            }
            return staleIds;
        }
    },


}
